import { create } from 'zustand';
import { loadJson, saveJson } from '@/lib/persist';

export interface GotoTarget {
  robotId: string;
  lat: number;
  lon: number;
  alt_m: number;
  ts_ms: number;
  /** Leg origin — where the drone (or the previous leg's target, for queued
   *  legs) was when this GOTO was issued. Anchors the static distance label
   *  and the traveled/planned progress readout. Absent on legacy-persisted
   *  or history-resent targets → label falls back to plain remaining. */
  from?: { lat: number; lon: number };
}

const HISTORY_MAX = 8;
const SETTINGS_KEY = 'fabricos.gotoSettings.v1';
const STATE_KEY = 'fabricos.gotoState.v1';

interface PersistedState {
  targets: Record<string, GotoTarget>;
  queues: Record<string, GotoTarget[]>;
  history: Record<string, GotoTarget[]>;
}

const EMPTY_STATE: PersistedState = { targets: {}, queues: {}, history: {} };

function loadState(): PersistedState {
  return loadJson<PersistedState>(STATE_KEY, EMPTY_STATE);
}

function saveState(s: PersistedState) {
  saveJson(STATE_KEY, s);
}

interface GotoSettings {
  /** Default altitude in metres AGL used by the click handler when no
   *  per-click override is provided. Editable via the in-map alt picker. */
  defaultAltAglM: number;
  /** Soft geofence radius around `home`. If > 0 and the click point lies
   *  outside this radius from any selected drone's home, the click is
   *  blocked (or confirmed) and the target marker draws with a red ring.
   *  0 means "no fence". */
  fenceRadiusM: number;
  /** Far-target confirm threshold from the drone's current position. If
   *  the click is further than this, a confirm prompt fires. 0 disables. */
  confirmRadiusM: number;
}

interface GotoTargetsStore {
  /** robotId → active GOTO. One target per asset; setting a new GOTO
   *  replaces the previous active (and pushes the displaced one to history). */
  targets: Record<string, GotoTarget>;
  /** robotId → pending queue tail (FIFO). Head promoted to `targets`
   *  automatically when the active one is reached/cleared. */
  queues: Record<string, GotoTarget[]>;
  /** robotId → last N reached/cancelled targets, newest first. */
  history: Record<string, GotoTarget[]>;
  /** Operator preferences for GOTO behaviour. Persisted to localStorage. */
  settings: GotoSettings;

  /** Overwrite the active target. Displaces (history) the old active and
   *  WIPES the queue — semantics: "go here NOW, ignore the rest." */
  set: (t: GotoTarget) => void;
  /** Append to the queue. If no active, promotes to active. */
  append: (t: GotoTarget) => void;
  /** Move the active to history; promote the queue head if any. Used by
   *  the arrival watcher in MapView. */
  advance: (robotId: string) => void;
  /** Clear everything (active + queue) for one robot. Old active is pushed
   *  to history. Doesn't touch the firmware — `cancelGoto()` is the caller
   *  that pairs this with a LOITER mode change. */
  clear: (robotId: string) => void;
  /** Wipe across all robots. Same caveats as `clear`. */
  clearAll: () => void;
  /** Patch the ACTIVE target's altitude in place — same destination, new
   *  height. Unlike `set()` this keeps the queue. No-op without an active. */
  setAlt: (robotId: string, altM: number) => GotoTarget | null;
  /** Re-send a historical target as a brand new active GOTO. */
  resend: (robotId: string, t: GotoTarget) => GotoTarget;
  /** Patch settings; persists to localStorage. */
  setSettings: (patch: Partial<GotoSettings>) => void;
}

const DEFAULTS: GotoSettings = {
  defaultAltAglM: 15,
  fenceRadiusM: 0,
  confirmRadiusM: 5000,
};

function loadSettings(): GotoSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<GotoSettings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

function persistSettings(s: GotoSettings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* quota / private mode — drop */
  }
}

function pushHistory(
  history: Record<string, GotoTarget[]>,
  robotId: string,
  t: GotoTarget,
): Record<string, GotoTarget[]> {
  const cur = history[robotId] ?? [];
  return { ...history, [robotId]: [t, ...cur].slice(0, HISTORY_MAX) };
}

const INITIAL_STATE = loadState();

export const useGotoTargets = create<GotoTargetsStore>((set, get) => ({
  targets: INITIAL_STATE.targets,
  queues: INITIAL_STATE.queues,
  history: INITIAL_STATE.history,
  settings: loadSettings(),

  set: (t) =>
    set((s) => {
      const prev = s.targets[t.robotId];
      let history = s.history;
      if (prev) history = pushHistory(history, t.robotId, prev);
      const queues = { ...s.queues };
      delete queues[t.robotId];
      return {
        targets: { ...s.targets, [t.robotId]: t },
        queues,
        history,
      };
    }),

  append: (t) =>
    set((s) => {
      const active = s.targets[t.robotId];
      if (!active) {
        return { targets: { ...s.targets, [t.robotId]: t } };
      }
      const cur = s.queues[t.robotId] ?? [];
      return { queues: { ...s.queues, [t.robotId]: [...cur, t] } };
    }),

  advance: (robotId) =>
    set((s) => {
      const prev = s.targets[robotId];
      const queue = s.queues[robotId] ?? [];
      const history = prev ? pushHistory(s.history, robotId, prev) : s.history;
      const targets = { ...s.targets };
      delete targets[robotId];
      const queues = { ...s.queues };
      delete queues[robotId];
      if (queue.length > 0) {
        const [next, ...rest] = queue;
        targets[robotId] = next!;
        if (rest.length > 0) queues[robotId] = rest;
      }
      return { targets, queues, history };
    }),

  clear: (robotId) =>
    set((s) => {
      const prev = s.targets[robotId];
      let history = s.history;
      if (prev) history = pushHistory(history, robotId, prev);
      const targets = { ...s.targets };
      delete targets[robotId];
      const queues = { ...s.queues };
      delete queues[robotId];
      return { targets, queues, history };
    }),

  clearAll: () =>
    set((s) => {
      let history = s.history;
      for (const [robotId, t] of Object.entries(s.targets)) {
        history = pushHistory(history, robotId, t);
      }
      return { targets: {}, queues: {}, history };
    }),

  setAlt: (robotId, altM) => {
    const cur = get().targets[robotId];
    if (!cur) return null;
    const next: GotoTarget = { ...cur, alt_m: altM };
    set((s) => ({ targets: { ...s.targets, [robotId]: next } }));
    return next;
  },

  resend: (robotId, t) => {
    const fresh: GotoTarget = { ...t, robotId, ts_ms: Date.now() };
    get().set(fresh);
    return fresh;
  },

  setSettings: (patch) =>
    set((s) => {
      const next = { ...s.settings, ...patch };
      persistSettings(next);
      return { settings: next };
    }),
}));

// Persist targets/queues/history whenever they change. Settings has its own
// persistence path (see setSettings). Fleet roster is transient — a target
// that references a robot which isn't online after reload just sits until the
// robot reconnects or the operator clears it.
useGotoTargets.subscribe((s) => {
  saveState({ targets: s.targets, queues: s.queues, history: s.history });
});

/** Great-circle distance in metres (Haversine). */
export function distMeters(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const R = 6_371_000;
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const dφ = ((b.lat - a.lat) * Math.PI) / 180;
  const dλ = ((b.lon - a.lon) * Math.PI) / 180;
  const h =
    Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Initial bearing from a → b in degrees, 0–360 (0 = north, 90 = east). */
export function bearingDeg(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const dλ = ((b.lon - a.lon) * Math.PI) / 180;
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
